import React, { useState, useMemo } from 'react'
import { getActions, updateAction, toggleActionPin, ActionItem } from '../lib/api'
import { useApi } from '../hooks/useApi'
import ContextChat from '../components/ContextChat'
import PullToRefresh from '../components/PullToRefresh'
import OverflowMenu from '../components/OverflowMenu'

const STATUS_FILTERS = [
  { label: 'Active', params: {} },
  { label: 'Waiting', params: { status: 'waiting' } },
  { label: 'Stale', params: { stale: 'true' } },
  { label: 'Done', params: { status: 'done' } },
]

function urgencyBadge(urgency: string) {
  const colors: Record<string, string> = {
    critical: 'var(--critical)',
    high: 'var(--high)',
    normal: 'var(--normal)',
    low: 'var(--low)',
  }
  return (
    <span style={{
      fontSize: 11,
      fontWeight: 600,
      color: colors[urgency] || 'var(--text-muted)',
      textTransform: 'uppercase',
    }}>
      {urgency}
    </span>
  )
}

export default function ActionsPage() {
  const [filterIdx, setFilterIdx] = useState(0)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set())
  const params = STATUS_FILTERS[filterIdx].params as Record<string, string>

  const { data, loading, refresh } = useApi<ActionItem[]>(
    () => getActions(params),
    [filterIdx]
  )

  const allChecked = !!data && data.length > 0 && data.every(i => checkedIds.has(i.id))

  const handleDone = async (id: number) => {
    await updateAction(id, { status: 'done' })
    refresh()
  }

  const handleDismiss = async (id: number) => {
    await updateAction(id, { status: 'dismissed' })
    refresh()
  }

  const handlePin = async (id: number) => {
    await toggleActionPin(id)
    refresh()
  }

  function toggleCheck(id: number) {
    setCheckedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleCheckAll() {
    if (allChecked) setCheckedIds(new Set())
    else setCheckedIds(new Set(data!.map(i => i.id)))
  }

  async function bulkUpdate(status: string) {
    await Promise.all(Array.from(checkedIds).map(id => updateAction(id, { status })))
    setCheckedIds(new Set())
    refresh()
  }

  async function bulkPin() {
    await Promise.all(Array.from(checkedIds).map(id => toggleActionPin(id)))
    setCheckedIds(new Set())
    refresh()
  }

  const selectedItem = selectedId && data ? data.find(i => i.id === selectedId) : null

  const selectedItemsForChat = useMemo(() => {
    if (checkedIds.size === 0) return undefined
    return Array.from(checkedIds).map(id => {
      const item = data?.find(a => a.id === id)
      return { type: 'action_item', id, label: item?.title.slice(0, 80) || `Action #${id}` }
    })
  }, [checkedIds, data])

  return (
    <div style={{ paddingBottom: 120 }}>
      <PullToRefresh onRefresh={refresh} />
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {STATUS_FILTERS.map((f, i) => (
          <button
            key={f.label}
            className={`nav-tab ${filterIdx === i ? 'active' : ''}`}
            style={{ padding: '6px 12px', fontSize: 13, borderRadius: 6 }}
            onClick={() => setFilterIdx(i)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Bulk action bar */}
      {checkedIds.size > 0 && (
        <div className="bulk-bar">
          <label className="bulk-select-all">
            <input type="checkbox" checked={allChecked} onChange={toggleCheckAll} />
            <span>{checkedIds.size} selected</span>
          </label>
          <div className="bulk-actions">
            <button onClick={() => bulkUpdate('done')}>Done</button>
            <button onClick={bulkPin}>Flag</button>
            <button className="danger" onClick={() => bulkUpdate('dismissed')}>Dismiss</button>
            <button className="muted" onClick={() => setCheckedIds(new Set())}>Clear</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading actions...</div>
      ) : !data || data.length === 0 ? (
        <div className="empty-state">
          <div className="message">No action items.</div>
        </div>
      ) : (
        data.map((item) => (
          <div key={item.id} className="feed-row">
            <input
              type="checkbox"
              className="feed-checkbox"
              checked={checkedIds.has(item.id)}
              onChange={() => toggleCheck(item.id)}
              onClick={e => e.stopPropagation()}
            />
            <div
              className={`action-item${selectedId === item.id ? ' selected' : ''}`}
              style={{ flex: 1 }}
              onClick={() => setSelectedId(prev => prev === item.id ? null : item.id)}
            >
              <div className={`action-status ${item.status}`} />
              <div className="action-content">
                <div className="action-header">
                  <div className="title">{item.title}</div>
                  <OverflowMenu actions={[
                    { label: '✓ Done', onClick: () => handleDone(item.id), disabled: item.status === 'done' || item.status === 'dismissed' },
                    { label: '✕ Dismiss', onClick: () => handleDismiss(item.id), disabled: item.status === 'done' || item.status === 'dismissed', danger: true },
                    { label: item.pinned ? '⚑ Unflag' : '⚑ Flag', onClick: () => handlePin(item.id) },
                  ]} />
                </div>
                <div className="meta">
                  {urgencyBadge(item.urgency)}
                  {item.client_name && <span>{item.client_name}</span>}
                  {item.waiting_on && <span>Waiting: {item.waiting_on}</span>}
                  {item.due_date && <span>Due: {item.due_date}</span>}
                  <span>Source: {item.source_type}</span>
                </div>
                {item.description && (
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>
                    {item.description}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))
      )}

      <ContextChat
        view="actions"
        selectedItem={selectedItem ? {
          type: 'action_item',
          id: selectedItem.id,
          label: selectedItem.title.slice(0, 40),
        } : null}
        selectedItems={selectedItemsForChat}
      />
    </div>
  )
}
